<template>
    <v-container fluid>
        <v-row>
            <v-col cols="12">
                <h1 class="text-left">GPS Metrics Over Time for <b v-if="device">{{ device.slno }}</b></h1>
            </v-col>
            <v-col cols="4">
                <v-select v-model="selectedDuration"
                          :items="duration"
                          :loading="processing"
                          label="Select Duration"
                          @update:model-value="fetchMetrics"/>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-progress-circular v-if="processing" color="primary" indeterminate/>
                <canvas ref="gpsChart"/>

                <v-btn v-if="chart" class="float-left" color="black" density="compact"
                       variant="outlined" @click="chart.resetZoom()">Reset Zoom
                </v-btn>
            </v-col>
        </v-row>
    </v-container>
</template>
<script setup>
import {ref, onMounted} from 'vue';
import {useRouter} from "vue-router";
import zoomPlugin from 'chartjs-plugin-zoom';
import {send} from "@/composables/apiClient";
import {Chart, registerables} from 'chart.js';
import {useSnackbarStore} from "@/stores/snackBarStore";

Chart.register(...registerables, zoomPlugin);

const router = useRouter()
const assetId = ref(router.currentRoute.value?.params?.asset ?? null)

const selectedDuration = ref('one_week');
const {showSnackbar} = useSnackbarStore();
const processing = ref(false);
const gpsChart = ref(null);
const device = ref(null);
const metrics = ref(null);
let chart;
const duration = [
    {value: 'one_week', title: '1 Week'},
    {value: 'two_week', title: '2 Week'},
    {value: 'one_month', title: '1 Month'},
    {value: 'two_month', title: '2 Month'},
    {value: 'three_month', title: '3 Month'},
    {value: 'six_month', title: '6 Months'},
    {value: 'one_year', title: '1 Year'},
    {value: 'two_year', title: '2 Year'},
    {value: 'five_year', title: '5 Year'},
    {value: 'all', title: 'All'},
];
const options = {
    responsive: true,
    interaction: {mode: 'index', intersect: false},
    plugins: {
        legend: {display: true, position: 'top'},
        tooltip: {mode: 'index', intersect: false},
        title: {display: true, text: 'GPS Metrics Over Time'},
        zoom: {
            pan: {
                mode: 'xy',
                enabled: true,
            },
            zoom: {
                wheel: {enabled: true},
                pinch: {enabled: true},
                mode: 'xy',
            }
        }
    },
    scales: {
        x: {
            title: {display: true, text: 'Time'},
            ticks: {maxRotation: 45, minRotation: 45}
        },
        y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {display: true, text: 'Time (s)'},
            beginAtZero: true,
        },
        y1: {
            type: 'linear',
            display: true,
            position: 'right',
            title: {display: true, text: 'Accuracy (m)'},
            beginAtZero: true,
            grid: {drawOnChartArea: false},
        }
    }
};

const renderChart = () => {
    const times = metrics.value?.map(m => m.event_time);
    const ttffData = metrics.value?.map(m => m.ttff);
    const ttbfData = metrics.value?.map(m => m.ttbf);
    const accuracyData = metrics.value?.map(m => m.accuracy);

    if (chart) {
        chart.destroy();
    }

    chart = new Chart(gpsChart.value, {
        type: 'line',
        data: {
            labels: times,
            datasets: [
                {
                    fill: false,
                    data: ttffData,
                    label: 'TTFF (s)',
                    borderColor: '#4CAF50',
                    backgroundColor: 'rgba(76,175,80,0.1)',
                    yAxisID: 'y',
                    pointRadius: 2,
                    borderWidth: 1.5,
                },
                {
                    fill: false,
                    data: ttbfData,
                    label: 'TTBF (s)',
                    borderColor: '#FF9800',
                    backgroundColor: 'rgba(255,152,0,0.1)',
                    yAxisID: 'y',
                    pointRadius: 2,
                    borderWidth: 1.5,
                },
                {
                    fill: false,
                    data: accuracyData,
                    label: 'Accuracy (m)',
                    borderColor: '#F44336',
                    backgroundColor: 'rgba(244,67,54,0.1)',
                    yAxisID: 'y1',
                    pointRadius: 2,
                    borderWidth: 1.5,
                },
            ],
        },
        options: options
    });
    chart.resetZoom();
}

const fetchMetrics = async () => {
    if (!assetId.value)
        return
    processing.value = true;
    send('get', `devices/${assetId.value}/gps-metrics`, {duration: selectedDuration.value})
        .then((response) => {
            device.value = response.data.data.device;
            metrics.value = response.data.data.metrics;
            renderChart();
        }).catch(() => {
        showSnackbar('GPS metrics could not be loaded', 'error')
    }).finally(() => processing.value = false)
}
onMounted(fetchMetrics);
</script>

<style scoped>
.main-container {
    height: calc(100vh - 100px) !important;
    overflow-y: hidden;
}
</style>
